import { useState } from 'react';
import { useMutation } from 'convex/react';

import { api } from '../../../convex/_generated/api';
import type { Id } from '../../../convex/_generated/dataModel';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/components/ui/use-toast';
import { friendlyAuthError } from '@/lib/auth-errors';

interface PreForgeBriefDialogProps {
  /** Whether the job is currently parked in `forge_awaiting_pre_input`. */
  open: boolean;
  jobId: Id<'jobs'>;
}

const MAX = 4000;

/**
 * Pre-FORGE checkpoint modal.
 *
 * After the user approves the LUMEN review, the orchestrator parks the
 * job in `forge_awaiting_pre_input` instead of jumping straight to
 * code generation. This dialog opens automatically and collects a
 * free-form note ("anything else FORGE should know?"). Submitting it
 * calls the `submitPreForgeBrief` mutation, which atomically persists
 * the brief on the job row and flips state to `forge_running` so the
 * orchestrator picks it up on its next poll.
 *
 * Cannot be dismissed without explicit action — the only way past this
 * modal is to either submit a brief or click "Skip and start". This
 * matches the pipeline UX: there is always one foreground action.
 */
export function PreForgeBriefDialog({ open, jobId }: PreForgeBriefDialogProps) {
  const { token } = useAuth();
  const { toast } = useToast();
  const submit = useMutation(api.jobs.submitPreForgeBrief);

  const [brief, setBrief] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const remaining = MAX - brief.length;

  const send = async (finalBrief: string) => {
    if (!token) return;
    setSubmitting(true);
    try {
      await submit({ token, jobId, brief: finalBrief });
      toast({
        title: 'Sent to FORGE',
        description: finalBrief
          ? 'Your brief will guide code generation.'
          : 'Generation started without additional notes.',
      });
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Could not start FORGE',
        description: friendlyAuthError(err),
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open}>
      <DialogContent
        className="max-w-xl"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="font-serif text-display-sm italic text-ink">
            One last note for FORGE?
          </DialogTitle>
          <DialogDescription className="text-graphite">
            Before we generate code: anything specific you'd like FORGE to keep
            in mind? Tone, copy, ordering, behaviour — small notes are fine.
            This is optional; you can skip and start.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-2">
          <Textarea
            value={brief}
            onChange={(e) => setBrief(e.target.value.slice(0, MAX))}
            rows={6}
            placeholder="e.g. Make the hero section more concise. Use the second project as the lead. Keep the contact form simple — no captcha."
            disabled={submitting}
            aria-label="Pre-FORGE brief"
          />
          <p className="font-mono text-[11px] text-graphite text-right tabular-nums">
            {remaining} characters left
          </p>
        </div>

        <DialogFooter className="gap-2 sm:justify-between">
          <Button
            variant="ghost"
            onClick={() => void send('')}
            disabled={submitting}
          >
            Skip and start
          </Button>
          <Button
            variant="accent"
            onClick={() => void send(brief.trim())}
            disabled={submitting}
          >
            {submitting ? 'Starting…' : 'Send to FORGE'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
